// views/Profile/components/BillingOverviewCard.jsx
import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import bigZenitCoin from '../../../assets/big-zenit-coin.webp';
import { estimateNextReset } from '../utils/billingCycle';
import './BillingOverviewCard.css';

const nf = new Intl.NumberFormat('en-US');
const formatCoins = (v) => (v == null || Number.isNaN(v) ? '—' : nf.format(Math.round(v)));

const formatResetDate = (anchorDate, lastResetAt) => {
  const next = estimateNextReset({ anchorDate, lastResetAt });
  if (!next) return null;
  return next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Todo el dinero de la cuenta en una sola pieza.
 *
 * Eran tres tarjetas —saldo, plan, consumo— y antes de eso un número suelto
 * flotando entre la cabecera y las pestañas, sin pertenecer a ninguna de las
 * dos. El problema no era la jerarquía: era que se estaban tratando como
 * tres módulos independientes tres cosas que son **una sola resta**.
 *
 *     5.000 de asignación − 137 usados = 4.863 disponibles
 *
 * Contadas por separado, quien lee tiene que hacer la cuenta para saber si
 * los números se corresponden. Contadas juntas, la cuenta ya está hecha.
 *
 * El orden es el de lo que alguien necesita saber, y no el del modelo de
 * datos: cuánto tengo, cuánto llevo gastado de cuánto, de qué plan sale y
 * cuándo vuelve a empezar.
 *
 * **Barra y no rosquilla.** Con consumos bajos —3%— el arco casi no se ve,
 * el círculo se come media tarjeta y hay que leer el número igual. Una barra
 * dice lo mismo en una línea, y deja sitio para que el resto de la historia
 * quepa en la misma pieza.
 */
export default function BillingOverviewCard({
  totalBalance = null,
  monthlyAllowance = null,
  totalCharged = null,
  planName = null,
  anchorDate = null,
  lastResetAt = null,
  loading = false,
  error = null,
  refreshing = false,
  onRefresh,
}) {
  if (loading) {
    return (
      <section className="billing-overview billing-overview--loading" aria-busy="true">
        <span className="billing-overview__label">Balance</span>
        <div className="billing-overview__skeleton billing-overview__skeleton--hero" />
        <div className="billing-overview__skeleton billing-overview__skeleton--line" />
        <div className="billing-overview__skeleton billing-overview__skeleton--bar" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="billing-overview billing-overview--error">
        <span className="billing-overview__label">Balance</span>
        <p className="billing-overview__error">
          <AlertCircle size={16} aria-hidden="true" />
          {error.message || 'Unable to load your balance.'}
        </p>
        {onRefresh && (
          <button
            type="button"
            className="billing-overview__retry"
            onClick={onRefresh}
            disabled={refreshing}
          >
            Try again
          </button>
        )}
      </section>
    );
  }

  // Sobre lo consumido y la asignación, que es la cuenta que el backend
  // hace. La versión anterior restaba el saldo del período a la asignación,
  // y con coins comprados por encima daba negativo: la tarjeta decía "0%
  // usado" al lado de "11.012 de 5.000".
  const hasAllowance = monthlyAllowance != null && monthlyAllowance > 0;
  const usedPercent = hasAllowance
    ? Math.min(100, Math.max(0, ((totalCharged ?? 0) / monthlyAllowance) * 100))
    : null;
  const resetOn = formatResetDate(anchorDate, lastResetAt);

  return (
    <section className="billing-overview">
      <div className="billing-overview__top">
        <span className="billing-overview__label">Balance</span>
        {onRefresh && (
          <button
            type="button"
            className="billing-overview__refresh"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh the balance"
          >
            <RefreshCw size={14} aria-hidden="true" />
          </button>
        )}
      </div>

      <p className="billing-overview__hero">
        <img
          src={bigZenitCoin}
          alt=""
          aria-hidden="true"
          className="billing-overview__coin"
        />
        <strong>{formatCoins(totalBalance)}</strong>
        <span>coins available</span>
      </p>

      {hasAllowance && (
        <div className="billing-overview__meter">
          <div className="billing-overview__meter-head">
            <span>
              {formatCoins(totalCharged ?? 0)} used of{' '}
              {formatCoins(monthlyAllowance)}
            </span>
            <span className="billing-overview__meter-pct">
              {Math.round(usedPercent)}%
            </span>
          </div>
          <div
            className="billing-overview__bar"
            role="progressbar"
            aria-valuenow={Math.round(usedPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Allowance used this period"
          >
            <span style={{ width: `${usedPercent.toFixed(1)}%` }} />
          </div>
        </div>
      )}

      {(planName || resetOn) && (
        <footer className="billing-overview__foot">
          {planName && <span>{planName}</span>}
          {resetOn && <span>Resets {resetOn}</span>}
        </footer>
      )}
    </section>
  );
}
